import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import AlgebraActivityWrapper from '~/bootcamp/Activities/Algebra/Components/AlgebraActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import { removePointsAndParenthesis } from '~/bootcamp/Activities/Algebra/Algebra.utils'
import Input from '~/bootcamp/Activities/components/Input'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useVariableSub from './useVariableSub'

function VariableSub({
  answerState,
  actionButtonsFC,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const {
    variable,
    equationCoefficients,
    formValid,
    inputRef,
    answer,
    onInputChange,
    handleCheckAnswer,
    goToNextQuestion,
  } = useVariableSub({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
    answerState,
    autofocus,
  })

  // The input is one bare box for the whole term, not just the coefficient: no trailing variable.
  useEmitInstance(
    onInstanceChange,
    equationCoefficients.length > 0
      ? `${equationCoefficients
          .map((coefficient) => `${coefficient > 1 ? coefficient : ''}${variable}`)
          .join(' - ')} = \\square`
      : null,
  )

  const [inputKey, setInputKey] = useState<string>(uuid())

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }

  return (
    <ActivityWrapper>
      <AlgebraActivityWrapper className="flex-col">
        <div className="flex flex-row flex-wrap justify-center items-center mt-2">
          {equationCoefficients.map((coefficient, i) => (
            <span className="tracking-widest" key={`${coefficient}${i.toString()}`}>
              {i > 0 && <span className="mx-1">-</span>}
              {coefficient > 1 && coefficient}
              <i>{variable}</i>
            </span>
          ))}
          <span className="block sm:inline-block">
            <Equals />
            <Input
              aria-label={t('ariaLabelButtons.insertValue')}
              autoFocus={autofocus}
              isError={answerState === false}
              key={inputKey}
              maxDigits={6}
              onChange={(value) => onInputChange(removePointsAndParenthesis(value))}
              onEnter={onEnterPress}
              ref={inputRef}
              type="text"
              value={answer}
            />
          </span>
        </div>
      </AlgebraActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(''),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default VariableSub
